import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ticketsService from '../services/ticketsService';
import { ticketsKeys } from '../queryKeys';

// Las respuestas del solicitante entran por email y las ingesta el cron en el
// servidor (cada pocos minutos), no una acción del agente. Sin refresco activo,
// el detalle abierto se queda con la caché y esas respuestas (y sus adjuntos) no
// aparecen hasta recargar la página. Por eso comentarios y adjuntos refrescan
// solos con polling y al volver a enfocar la ventana (venir del cliente de
// correo). El resto de queries mantienen el refetchOnWindowFocus: false global.
const REFRESCO_DETALLE_MS = 20 * 1000;

// Comentarios (conversacion) de un ticket.
export const useTicketComentarios = (headquarter, id, soloPublicos = false) =>
  useQuery({
    queryKey: ticketsKeys.comentarios(headquarter, id, soloPublicos),
    queryFn: async () => {
      const data = await ticketsService.listarComentarios({ headquarter, id, soloPublicos });
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(headquarter && id),
    refetchInterval: REFRESCO_DETALLE_MS,
    refetchOnWindowFocus: true,
  });

// Adjuntos de un ticket.
export const useTicketAdjuntos = (headquarter, id) =>
  useQuery({
    queryKey: ticketsKeys.adjuntos(headquarter, id),
    queryFn: async () => {
      const data = await ticketsService.listarAdjuntos({ headquarter, id });
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(headquarter && id),
    refetchInterval: REFRESCO_DETALLE_MS,
    refetchOnWindowFocus: true,
  });

// Crear comentario. Invalida todo lo del ticket (prefijo detalle, que cubre
// comentarios/adjuntos/historial) y las listas (cambia actualizado_en).
export const useCrearComentario = (headquarter, id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => ticketsService.crearComentario({ headquarter, id, ...body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ticketsKeys.detalle(headquarter, id) });
      qc.invalidateQueries({ queryKey: ['tickets', 'lista'] });
    },
  });
};

// Subir adjunto (base64). Cuelga de un comentario (comentario_id).
export const useSubirAdjunto = (headquarter, id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => ticketsService.subirAdjunto({ headquarter, id, ...body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ticketsKeys.adjuntos(headquarter, id) }),
  });
};

// Eliminar adjunto.
export const useEliminarAdjunto = (headquarter, id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (adjuntoId) => ticketsService.eliminarAdjunto({ headquarter, id, adjuntoId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ticketsKeys.adjuntos(headquarter, id) }),
  });
};
